export default class DataFactory {
    private readonly schema: Schema;

    constructor(schema: Schema) {
        this.schema = Object.assign(new Schema(), schema);
    }

    public create(idData: Data, tableId: string): DataObserver {
        const table = this.schema.findTable(tableId)!;
        return this.recursionCreate(idData, table);
    }

    public recursionCreate(idData: Data, table: Table): DataObserver {
        const aliasData = new DataObserver(idData);

        for (const column of table.columns) {
            Object.defineProperty(aliasData, column.columnName, {
                enumerable: true,
                get() {
                    return idData[column.id];
                },
                set(value) {
                    idData[column.id] = value;
                },
            });
        }

        const relations = this.schema.findRelations(table.id);
        for (const relation of relations) {
            const factory = this;
            const foreignTable = this.schema.findTable(relation.foreignTable)!;

            if (relation.relationType === 'OneToOne') {
                Object.defineProperty(aliasData, relation.id, {
                    enumerable: true,
                    get() {
                        return factory.recursionCreate(idData[relation.id] as Data, foreignTable);
                    },
                    set(newValue) {
                        idData[relation.id] = factory.unwrap(newValue, foreignTable);
                    },
                });
            } else {
                Object.defineProperty(aliasData, relation.id, {
                    enumerable: true,
                    get() {
                        return new Proxy(idData[relation.id] as Data[], new ArrayObserver(factory, foreignTable));
                    },
                    set(newValue) {
                        idData[relation.id] = newValue.map((it: any) => factory.unwrap(it, foreignTable));
                    },
                });
            }
        }

        return aliasData;
    }

    public unwrap(aliasData: DataObserver, table: Table): Data {
        const idData: Data = {};
        for (const column of table.columns) {
            if (aliasData.hasOwnProperty(column.columnName)) {
                idData[column.id] = aliasData[column.columnName];
            }
        }
        return idData;
    }
}

interface Data {
    [key: string]: number | string | Data | Data[];
}

// tslint:disable-next-line: max-classes-per-file
class DataObserver implements Data {
    public readonly target: Data;
    // private readonly cached: Map<string, DataObserver | ArrayObserver>;

    constructor(target: Data) {
        this.target = target;
        // this.cached = new Map<string, DataObserver | ArrayObserver>();
    }

    [key: string]: string | number | Data | Data[];
}

// tslint:disable-next-line: max-classes-per-file
class ArrayObserver implements ProxyHandler<Data[]> {
    private readonly factory: DataFactory;
    private readonly table: Table;

    constructor(factory: DataFactory, table: Table) {
        this.factory = factory;
        this.table = table;
    }

    public get(target: Data[], prop: any): any {
        if (target.propertyIsEnumerable(prop)) {
            return this.factory.recursionCreate(target[prop], this.table);
        }
        return target[prop];
    }

    public set(pTarget: Data[], prop: any, value: any): boolean {
        if (pTarget.propertyIsEnumerable(prop) || pTarget[prop] === undefined) {
            if (value.target) {
                pTarget[prop] = value.target;
            } else {
                pTarget[prop] = this.factory.unwrap(value, this.table);
            }
        } else {
            pTarget[prop] = value;
        }
        return true;
    }
}

// tslint:disable-next-line: max-classes-per-file
class Schema {
    public tables: Table[] = [];
    public relations: Relation[] = [];

    public findTable(tableId: string): Table | undefined {
        return this.tables.find((it) => it.id === tableId);
    }
    public findRelations(tableId: string): Relation[] {
        return this.relations.filter((it) => it.primaryTable === tableId);
    }
}

interface Table {
    id: string;
    name: string;
    dbAlias: string;
    columns: Column[];
}

interface Column {
    id: string;
    name: string;
    columnName: string;
    dbAlias: string;
}

interface Relation {
    id: string;
    primaryTable: string;
    primaryKey: string;
    foreignTable: string;
    foreignKey: string;
    relationType: RelationType;
    dbAlias: string;
}

type RelationType = 'OneToOne' | 'OneToMany';
